import { Directive, ElementRef, Renderer2, Input } from '@angular/core';

@Directive({
    selector: '[appSetImageDimensions]',
    standalone: true,
})
export class SetImageDimensionsDirective {

    @Input() isProductListPage: boolean = false;

    private resizeTimeout: any;
    private lastWidth: number | null = null;
    private lastHeight: number | null = null;
    private sizeMultiplication = 2
    constructor(
        private el: ElementRef,
        private renderer: Renderer2
    ) { }

    ngAfterViewInit() {
        const imgElement = this.el.nativeElement;

        imgElement.onload = () => this.updateImageSrcAfterResize();

        if (imgElement.complete) {
            this.updateImageSrcAfterResize();
        }
    }

    /**
     * Updates the `src` attribute of an image element based on its resized dimensions and its parent's dimensions.
     * This method ensures the image dynamically adjusts its width and height attributes, and appends a transformation string to the `src` URL.
     */
    private updateImageSrcAfterResize(): void {
        const imgElement = this.el.nativeElement;
        let parentElement = imgElement.parentElement;

        if (parentElement && imgElement.complete) {
            const parentRect = this.isProductListPage ? parentElement.parentElement.getBoundingClientRect() : parentElement.getBoundingClientRect();
            const width = Math.round(parentRect.width);
            const height = Math.round(parentRect.height);

            if (this.isProductListPage && height > 0 && (height !== this.lastHeight)) {
                this.lastHeight = height;

                // Set the width and height attributes on the image element
                this.renderer.setAttribute(imgElement, 'height', `${height}`);

                // Update the image src with the transformation string `?tr=w-${width},h-${height}`
                this.updateImageSrc(imgElement, width, height);
            } else if (!this.isProductListPage && width > 0 && (width !== this.lastWidth)) {
                this.lastWidth = width;

                // Set the width and height attributes on the image element
                this.renderer.setAttribute(imgElement, 'width', `${width}`);

                // Update the image src with the transformation string `?tr=w-${width},h-${height}`
                this.updateImageSrc(imgElement, width, height);
            } else if (width > 0 && height > 0 && (width !== this.lastWidth || height !== this.lastHeight)) {
                this.lastWidth = width ;
                this.lastHeight = height;

                // Set the width and height attributes on the image element
                this.renderer.setAttribute(imgElement, 'width', `${width}`);
                this.renderer.setAttribute(imgElement, 'height', `${height}`);

                // Update the image src with the transformation string `?tr=w-${width},h-${height}`
                this.updateImageSrc(imgElement, width, height);
            }
        }
    }

    /**
     * Updates the `src` attribute of the provided image element with transformation parameters based on the specified width and height.
     * @param {HTMLImageElement} imgElement - The image element whose `src` attribute needs to be updated.
     * @param {number} width - The desired width for the image transformation.
     * @param {number} height - The desired height for the image transformation.
     */
    private updateImageSrc(imgElement: HTMLImageElement, width: number, height: number): void {

        let originalSrc = imgElement.src;

        // Check if the transformation is already applied to avoid unnecessary updates
        if (!this.isTransformationApplied(originalSrc, width, height)) {
            width = width * this.sizeMultiplication
            height = height * this.sizeMultiplication

            // Remove any existing transformations (if present)
            originalSrc = this.removeTransformation(originalSrc);

            // Append the new transformation parameters `tr=w-${width},h-${height}`
            const transformationParam = this.isProductListPage ? `tr=h-${height}` : `tr=w-${width}${height > 0 ? ',h-' + height : ''}`;
            const separator = originalSrc.includes('?') ? '&' : '?';
            let updatedSrc = `${originalSrc}${separator}${transformationParam}`;
            if (imgElement.alt === 'Outlet Flooring' || imgElement.alt === 'Outlet Walling') {
                updatedSrc = updatedSrc + ',fo-bottom';
            }
            updatedSrc = updatedSrc + ',q-100,f-webp,e-sharpen-50,lossless-1'

            // Set the updated src attribute on the image
            this.renderer.setAttribute(imgElement, 'src', updatedSrc);
        }
    }

    /**
     * Removes the "tr" transformation parameter from the given URL, if it exists.
     * @param {string} url - The URL from which the transformation parameter should be removed.
     */
    private removeTransformation(url: string): string {
        const urlObj = new URL(url);

        // Remove the "tr" query parameter if it exists
        if (urlObj.searchParams.has('tr')) {
            urlObj.searchParams.delete('tr');
        }

        return urlObj.toString();
    }

    /**
     * Checks if a specific transformation has been applied to the given URL based on the provided width and height.
     * @param {string} url - The URL to check for the transformation.
     * @param {number} width - The width value used for the transformation calculation.
     * @param {number} height - The height value used for the transformation calculation.
     */
    private isTransformationApplied(url: string, width: number, height: number): boolean {
        const urlObj = new URL(url);
        width = width * this.sizeMultiplication
        height = height * this.sizeMultiplication
        const transformationParam = this.isProductListPage ? `tr=h-${height}` : `tr=w-${width}${height > 0 ? ',h-' + height : ''}`;

        return urlObj.searchParams.has('tr') && urlObj.searchParams.get('tr') === transformationParam;
    }

    ngOnDestroy(): void {
        if (this.resizeTimeout) {
            clearTimeout(this.resizeTimeout);
        }
    }
}
